import { cn } from '@/lib/cn';
import type { PackArtKey } from '@chicare/shared';
import { PackArt } from '@/components/packs/PackArt';

/**
 * PackBox — the framed container a pack illustration sits in.
 *
 * A gradient from near-white to a pale blue, with the same 1px line border as
 * every other surface. Kept separate from PackArt so a real product photograph
 * can drop into the same frame later without every call site changing.
 */

export interface PackBoxProps {
  pack: PackArtKey;
  /** A real photograph, when the catalogue has one. Falls back to the drawing. */
  src?: string | null;
  alt?: string;
  className?: string;
  /** Square aspect, for grid tiles. */
  square?: boolean;
  /** Draw the art slightly smaller, for hero placements. */
  large?: boolean;
  rounded?: 'card' | 'lg' | 'md';
}

const radii = {
  card: 'rounded-[10px]',
  lg: 'rounded-[14px]',
  md: 'rounded-lg',
} as const;

export function PackBox({
  pack,
  src,
  alt,
  className,
  square = false,
  large = false,
  rounded = 'card',
}: PackBoxProps) {
  return (
    <div
      className={cn(
        'relative grid place-items-center overflow-hidden border border-line',
        'bg-[linear-gradient(160deg,#FBFCFE,#EEF3FA)]',
        radii[rounded],
        square && 'aspect-square',
        className,
      )}
    >
      {src ? (
        <img
          src={src}
          alt={alt ?? ''}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-contain"
        />
      ) : (
        <div className={cn(large ? 'h-[70%] w-[70%]' : 'h-[78%] w-[78%]')}>
          <PackArt name={pack} title={alt} />
        </div>
      )}
    </div>
  );
}
